import { Button, Dialog, Message } from '@alifd/next';
import type { InputRef } from 'antd';
import { Card, Cascader, Form, Input, Space, Typography } from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import PageHeader from '@/components/PageHeader';
import useService from '@/lib/hooks/useService';
import type { DepositRegistration } from '@/types/database/depositRegistration';
import type { ResidentAccountNumber } from '@/types/database/residentAccountNumbers';
import type { User } from '@/types/database/users';
import { generateHouseHoldNumberCascader } from '@/utils/generateHouseHoldNumberCascader';

import DepositItemsTable from './components/DepositItemsTable';
import styles from './index.module.scss';

const { useForm, Item } = Form;

type SearchType = 'barcode' | 'rfid' | 'household';

const searchTypes: {
  [key in SearchType]: {
    label: string;
    imgSrc: string;
    searchLabel: string;
  };
} = {
  barcode: {
    label: '掃描APP條碼',
    imgSrc: '/ezcon/img/Barcode.svg',
    searchLabel: '條碼值：',
  },
  rfid: {
    label: '掃描磁扣/卡片',
    imgSrc: '/ezcon/img/Rfid.svg',
    searchLabel: '磁扣值：',
  },
  household: {
    label: '直接領取',
    imgSrc: '/ezcon/img/Receive.svg',
    searchLabel: '戶號：',
  },
};

const DepositReceive: React.FC = () => {
  const [searchType, setSearchType] = useState<SearchType>('barcode');
  const [searchValue, setSearchValue] = useState<string>('');
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  const [houses] = useService<ResidentAccountNumber>({
    collectionName: 'residentAccountNumbers',
    query: {
      $select: ['householdNumber'],
    },
  });

  const [users] = useService<User>({
    collectionName: 'users',
    query: {
      $select: ['name', 'householdNumbers', 'barcodeType', 'rfidType'],
    },
  });

  const [depositItems, updateDepositItems, depositItemsClient] =
    useService<DepositRegistration>({
      collectionName: 'deposit',
    });

  const houseOptions = useMemo(
    () =>
      generateHouseHoldNumberCascader(
        houses.map((house) => house.householdNumber)
      ),
    [houses]
  );

  const [form] = useForm();

  const inputRef = useRef<InputRef>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [showSearchDialog]);

  const filterDataBySearchValue = useCallback(
    (args: {
      dataSource: DepositRegistration[];
      searchValue: string;
      searchType: SearchType;
    }) => {
      const { dataSource, searchValue, searchType } = args;

      if (searchType === 'household') {
        const filteredDataSource = dataSource.filter(
          (item) => item.household === searchValue
        );
        return filteredDataSource;
      }

      const newCurrentUser = users.find((user) => {
        if (searchType === 'barcode') {
          return user.barcodeType === searchValue;
        }
        if (searchType === 'rfid') {
          return user.rfidType?.includes(searchValue);
        }
        return false;
      });
      if (newCurrentUser) {
        const filteredDataSource = dataSource.filter((item) =>
          newCurrentUser.householdNumbers?.includes(item.household)
        );
        return filteredDataSource;
      } else {
        Message.error('查不到住戶資料');
        return [];
      }
    },
    [users]
  );

  const tableDataSource = useMemo(() => {
    let dataSource = depositItems;

    if (searchValue) {
      const filteredDataSource = filterDataBySearchValue({
        dataSource,
        searchValue,
        searchType,
      });
      dataSource = filteredDataSource;
    }

    return dataSource;
  }, [depositItems, filterDataBySearchValue, searchType, searchValue]);

  return (
    <div>
      <PageHeader
        title="領取物品"
        breadcrumbs={[{ name: '寄物管理' }, { name: '領取物品' }]}
      />
      <Space direction="vertical" style={{ width: '100%' }}>
        <Card>
          <Space direction="vertical" className={styles.searchContainer}>
            <strong className={styles.searchTitle}>請選擇掃描類型</strong>
            <Space className={styles.searchTypeContainer}>
              {Object.entries(searchTypes).map(([typeName, type]) => {
                return (
                  <Space key={typeName} direction="vertical">
                    <Typography.Title level={4}>{type.label}</Typography.Title>
                    <button
                      className={styles.searchTypeButton}
                      onClick={() => {
                        setSearchValue('');
                        setSearchType(typeName as SearchType);
                        setShowSearchDialog(true);
                        setCurrentUser(null);
                      }}
                    >
                      <img src={type.imgSrc} alt={type.label} />
                    </button>
                    <Space
                      style={{
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Typography.Text>{`${type.searchLabel}${
                        searchType === typeName ? searchValue : ''
                      }`}</Typography.Text>
                      {searchValue && searchType === typeName && (
                        <Button
                          onClick={() => {
                            setSearchValue('');
                            setCurrentUser(null);
                          }}
                        >
                          清除篩選
                        </Button>
                      )}
                    </Space>
                  </Space>
                );
              })}
            </Space>
          </Space>
        </Card>
        <DepositItemsTable
          data={tableDataSource}
          updateData={updateDepositItems}
          dataClient={depositItemsClient}
          currentUser={currentUser}
        />
      </Space>
      <Dialog
        title={
          <Typography.Title level={4} style={{ margin: 0 }}>
            {searchTypes[searchType].label}
          </Typography.Title>
        }
        visible={showSearchDialog}
        footer={false}
        onClose={() => setShowSearchDialog(false)}
      >
        <Space direction="vertical">
          <Space className={styles.searchTypeFormContainer}>
            <Form
              form={form}
              onFinish={(values) => {
                const value = values?.[searchType];
                if (value) {
                  if (searchType === 'household') {
                    setSearchValue(value.join('-'));
                  } else {
                    setSearchValue(value);
                  }
                }
                form.resetFields();
                setShowSearchDialog(false);
              }}
              onValuesChange={() => {
                form.validateFields();
              }}
            >
              {searchType !== 'household' && (
                <Item
                  name={searchType}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value && !/^[\d|a-zA-Z]+$/.test(value)) {
                          return Promise.reject('請輸入正確條碼值');
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    onPressEnter={form.submit}
                    placeholder={`${searchTypes[searchType].label}或手動輸入`}
                    style={{ width: 300 }}
                    ref={inputRef}
                  />
                </Item>
              )}
              {searchType === 'household' && (
                <Item name={searchType}>
                  <Cascader
                    options={houseOptions}
                    placeholder="選擇戶號"
                    style={{ width: 300 }}
                  />
                </Item>
              )}
            </Form>
            <Button onClick={form.submit}>確定</Button>
          </Space>
          <img
            className={styles.searchDialogImg}
            src={searchTypes[searchType].imgSrc}
            alt={searchType}
          />
        </Space>
      </Dialog>
    </div>
  );
};

export default DepositReceive;
